import { inject } from "vue"
import moment from "moment-timezone"
import type { Services } from "@services"
import { useTaskStore } from "@core/stores/task.store"
import type { OptimTurpeTemplateView } from "@modules/courtage/types/optim-turpe"
import { useOptimTurpeStore } from "@modules/courtage/stores/optim-turpe.store"

export type OptimTurpeReportRow = Pick<
    OptimTurpeTemplateView,
    "_id" | "url" | "expiredDate"
>

type RapportFields = {
    url_rapport: string
    date_expiration_rapport?: string
}

function readUrlFromRecord(rec: Record<string, unknown>): string | null {
    const v = rec.url_rapport
    if (typeof v === "string" && v.trim() !== "") {
        return v
    }
    return null
}

/**
 * Extrait URL + date d’expiration du callback `onSuccess` du store tâches
 * (`{ data: payload }` Pusher, JSON `data_url`, ou champs imbriqués `data` / `result`).
 */
function extractRapportFromTaskSuccess(raw: unknown): RapportFields | null {
    if (!raw || typeof raw !== "object") {
        return null
    }
    const envelope = raw as { data?: unknown }
    const inner = envelope.data
    if (!inner || typeof inner !== "object" || Array.isArray(inner)) {
        return null
    }
    const taskContent = inner as Record<string, unknown>

    const candidates: Array<Record<string, unknown> | null> = [
        taskContent,
        typeof taskContent.data === "object" &&
        taskContent.data !== null &&
        !Array.isArray(taskContent.data)
            ? (taskContent.data as Record<string, unknown>)
            : null,
        typeof taskContent.result === "object" &&
        taskContent.result !== null &&
        !Array.isArray(taskContent.result)
            ? (taskContent.result as Record<string, unknown>)
            : null,
    ]

    let url: string | null = null
    let dateExpiration: string | undefined

    for (const rec of candidates) {
        if (!rec) continue
        const u = readUrlFromRecord(rec)
        if (u) {
            url = u
            const d = rec.date_expiration_rapport
            if (typeof d === "string" && d.trim() !== "") {
                dateExpiration = d
            }
            break
        }
    }

    if (!url) {
        return null
    }
    return {
        url_rapport: url,
        ...(dateExpiration != null
            ? { date_expiration_rapport: dateExpiration }
            : {}),
    }
}

/**
 * Téléchargement du rapport PDF de synthèse (URL signée valide ou tâche `POST …/analyse/:id/rapport`).
 * Comportement aligné sur `useDownloadAnalyseReport` (homeys-legacy) ; après succès tâche,
 * mise à jour du store (`patchAnalyseRapport`) sans refetch GET optimturpe.
 */
export function useOptimTurpeDownloadReport() {
    const services = inject<Services>("services")
    const taskStore = useTaskStore()
    const optimTurpeStore = useOptimTurpeStore()

    /**
     * Ouvre le PDF si l’URL existe et n’est pas expirée ; sinon lance la génération et suit la tâche.
     */
    async function downloadOptimTurpeReport(
        analyse: OptimTurpeReportRow
    ): Promise<void> {
        const { _id, url, expiredDate } = analyse

        if (url && expiredDate && moment(expiredDate).isAfter(moment())) {
            window.location.assign(url)
            return
        }

        if (!services?.$generateOptimTurpeAnalyseReport) {
            return
        }

        try {
            const { task_id, event_name } =
                await services.$generateOptimTurpeAnalyseReport(_id)

            if (!task_id || !event_name) {
                return
            }

            taskStore.followEvent(task_id, event_name, {
                taskname: "analyse.optimturpe.rapport",
                taskToastTitle: "Génération du rapport PDF...",
                taskToastSuccessMessage: "Rapport PDF généré.",
                taskToastFailureMessage:
                    "Échec de la génération du rapport PDF.",
                notification: true,
                autoDelete: true,
                onSuccess: (raw: unknown) => {
                    const extracted = extractRapportFromTaskSuccess(raw)
                    if (!extracted) {
                        return
                    }
                    optimTurpeStore.patchAnalyseRapport({
                        analyseId: _id,
                        url_rapport: extracted.url_rapport,
                        date_expiration_rapport:
                            extracted.date_expiration_rapport,
                    })
                    window.location.assign(extracted.url_rapport)
                },
            })
        } catch {
            // Erreur déjà remontée par `handleServiceCall` / toast métier si besoin
        }
    }

    return {
        downloadOptimTurpeReport,
    }
}
